import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBetSlip } from "@/context/BetSlipContext";
import { Button } from "@/components/ui/button";
import { leagueLabel } from "@/components/sportsbook/SBBoardGame";
import { EventScoreboard, MarketTabs } from "@/components/sportsbook/EventScoreboard";
import GameMarketRow from "@/components/sportsbook/GameMarketRow";
import PlayerPropRow from "@/components/sportsbook/PlayerPropRow";
import MarketProps from "@/components/sportsbook/MarketProps";
import SGPBuilder from "@/components/sportsbook/SGPBuilder";
import { EventSkeleton } from "@/components/sportsbook/Skeletons";
import { sortSportsbookEvents } from "@/lib/sportsbookPriority";
import { buildPropCatalog, buildPopularPreview } from "@/lib/propCatalog";

const spreadTitle = (sport) => sport === "baseball" ? "Run line" : sport === "hockey" ? "Puck line" : sport === "soccer" ? "Handicap" : "Spread";

// Game-lines card header — same grid as GameMarketRow so the SPREAD/TOTAL/MONEYLINE
// labels line up over the price columns (SBX-03 §2.8).
function GameLinesHeader({ sport }) {
  const spread = spreadTitle(sport).toUpperCase();
  const Row = ({ mobile }) => (
    <div style={{ display: "grid", gridTemplateColumns: mobile ? "minmax(0,1fr) repeat(3,64px)" : "minmax(0,1fr) repeat(3,112px)", columnGap: mobile ? 5 : 8, alignItems: "center", height: mobile ? 40 : 50, padding: mobile ? "0 12px" : "0 20px", background: "#0F0F12", borderBottom: "1px solid #1A1A1E" }}>
      <span style={{ font: "800 14px Manrope", letterSpacing: ".1em" }}>Game lines</span>
      {[spread, "TOTAL", mobile ? "ML" : "MONEYLINE"].map((l) => (
        <span key={l} style={{ textAlign: "center", font: "700 10px Outfit", letterSpacing: ".16em", color: "#8A8A93" }}>{l}</span>
      ))}
    </div>
  );
  return (<><div className="hidden lg:block"><Row mobile={false} /></div><div className="lg:hidden"><Row mobile /></div></>);
}

// Prop category chip (SBX-04): active = white pill, inactive = surface chip. No raw inventory count —
// counts like "Passing 15" reflect internal data volume, not useful navigation, so the chip is just a
// clean category label (horizontal scroll preserved by the parent rail).
function PropChip({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className="flex h-9 shrink-0 items-center rounded-[11px] px-3.5 transition-colors focus-ring"
      style={active ? { background: "#FAFAFA", color: "#0A0A0A", font: "700 13px Outfit" } : { background: "#141417", boxShadow: "inset 0 0 0 1px #26262B", color: "#a1a1aa", font: "600 13px Outfit" }}>
      {label}
    </button>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { has, toggle, count } = useBetSlip();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [propCat, setPropCat] = useState(null);   // Player Props: active category (market-first)
  const [propSearch, setPropSearch] = useState(""); // Player Props: player/market search
  const [otherLive, setOtherLive] = useState([]);
  const [tab, setTab] = useState("odds"); // odds | props | sgp | stats
  const sectionRefs = useRef({});

  // Alive guard so a slow in-flight response for a PREVIOUS event id can't land after we've
  // navigated to a new event and flash stale markets (UIB-04, mirrors OutrightDetail).
  const aliveRef = useRef(true);

  const load = useCallback(async (silent) => {
    try {
      const { data: d } = await api.get(`/events/${id}/markets`);
      if (aliveRef.current) setData(d);
    } catch (e) {
      if (!silent && aliveRef.current) toast.error("Could not load this event");
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [id]);

  // Reset to the loading skeleton the moment the route id changes so the previous event's
  // markets never flash while the new event loads.
  useEffect(() => {
    aliveRef.current = true;
    setData(null);
    setLoading(true);
    load();
    const t = setInterval(() => load(true), 3000);
    return () => { aliveRef.current = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // other live games for the top strip
  useEffect(() => {
    const pull = () => api.get("/events").then(({ data: evs }) =>
      // premium live games first (P0-A) — priority-sort BEFORE limiting the strip to 8
      setOtherLive(sortSportsbookEvents((evs || []).filter((e) => e.status === "live" && String(e.id) !== String(id) && e.home_team && e.away_team)).slice(0, 8))
    ).catch(() => {});
    pull();
    const t = setInterval(pull, 5000);
    return () => clearInterval(t);
  }, [id]);

  const ev = data?.event;
  const groups = data?.groups || { moneyline: [], spread: [], total: [] };
  const players = data?.props_by_player || [];
  const locked = ev && !["open", "live", "paused"].includes(ev.status);
  const eventLabel = ev ? `${ev.away_team} @ ${ev.home_team}` : "";

  // A custom Market-Ops prop attached to this game is a separate event; wager against ITS id so
  // grading/settlement stays on the child market (o.event_id is set only for those merged outcomes).
  const choose = (o) => toggle({ eventId: o.event_id || id, outcomeId: String(o.id), label: o.label, odds: o.odds, marketVersion: o.market_version ?? null, line: o.line ?? null, side: o.side ?? null, receivedAt: o.received_at ?? null, providerUpdatedAt: o.provider_updated_at ?? null, expiresAt: o.expires_at ?? null, eventLabel, market: o.market, league: ev?.league || null, sport: ev?.sport || null, live: ev?.status === "live", propKey: o.market === "prop" ? `${o.player || ""}|${o.prop_type || o.prop_short || ""}|${o.line ?? ""}` : null });
  const picked = (oid) => has(id, oid);
  const pickedO = (o) => has(o.event_id || id, String(o.id));  // outcome-aware (handles custom child props)

  if (loading) return <div className={`px-4 py-6 ${count > 0 ? "lg:pr-[360px]" : ""}`} data-testid="event-loading"><EventSkeleton /></div>;
  if (!ev) return (
    <div className="mx-auto max-w-5xl px-4 py-16 text-center text-white">
      <p className="text-[#a1a1aa]">Event not found.</p>
      <Button onClick={() => navigate("/sportsbook")} className="mt-4 rounded-full" data-testid="event-back-empty">Back to board</Button>
    </div>
  );

  // markets flattened so the columnar game-lines block reads exactly like the board
  const lineEvent = { ...ev, id, outcomes: [...(groups.moneyline || []), ...(groups.spread || []), ...(groups.total || [])] };
  const hasLines = lineEvent.outcomes.length > 0;
  // MARKET-FIRST prop catalog — derived from props_by_player; the SAME outcome objects are carried
  // through unchanged (id / market_version / odds / line / side preserved), SGP still consumes the
  // original players array. (Plain const — must not be a hook here, after the early returns above.)
  const catalog = buildPropCatalog(players, ev.sport);
  const totalPropLines = catalog.markets.reduce((n, m) => n + m.lineCount, 0);
  // Popular preview: a curated cross-section of high-value markets, not just players.slice(0,4).
  const popularPreview = buildPopularPreview(players, ev.sport, 4);
  // Player Props tab state: active category + player/market search.
  const q = propSearch.trim().toLowerCase();
  const activeCat = catalog.categories.find((c) => c.key === propCat) || catalog.categories[0] || null;
  const searchMarkets = q ? catalog.markets.map((m) => {
    const labelHit = m.label.toLowerCase().includes(q);
    const plys = labelHit ? m.players : m.players.filter((pr) => String(pr.player).toLowerCase().includes(q));
    return plys.length ? { ...m, players: plys, playerCount: plys.length } : null;
  }).filter(Boolean) : null;
  const shownMarkets = searchMarkets || (activeCat ? activeCat.markets : []);

  return (
    <div className={`overflow-x-clip px-4 pt-6 pb-[120px] text-white lg:pb-6 ${count > 0 ? "lg:pr-[360px]" : ""}`} data-testid="event-detail-page">
      {/* breadcrumb */}
      <div className="mb-4 flex items-center gap-1.5 text-xs text-[#71717a]">
        <button onClick={() => navigate("/sportsbook")} data-testid="event-back-button" className="inline-flex items-center gap-1 rounded-md transition-colors hover:text-white focus-ring">
          <Icon name="arrow-left" size={14} /> Sportsbook
        </button>
        <span>/</span>
        <span className="uppercase tracking-wide">{leagueLabel(ev.league) || ev.sport}</span>
        <span>/</span>
        <span className="truncate text-[#a1a1aa]">{ev.away_team} @ {ev.home_team}</span>
      </div>

      <EventScoreboard event={ev} liveGames={otherLive} />

      {locked && <p className="mt-4 rounded-xl border border-[#1F1F23] bg-[#101013] px-4 py-3 text-sm text-[#a1a1aa]" data-testid="event-locked">Betting is closed for this event. Open bets settle automatically — see My Bets.</p>}

      {/* Market tabs — underline style; only render tabs that have data (Stats removed, bug #7) */}
      <div className="mt-5">
        <MarketTabs
          active={tab === "odds" ? "popular" : tab}
          onChange={(k) => setTab(k === "popular" ? "odds" : k)}
          tabs={[
            { key: "popular", label: "Popular", testid: "event-tab-odds" },
            ...(players.length ? [{ key: "props", label: "Player Props", count: players.length, testid: "event-tab-props" }] : []),
            { key: "sgp", label: "Same Game Parlay", testid: "event-tab-sgp" },
          ]}
        />
      </div>

      {/* POPULAR — Featured markets: game lines card + player-props preview + SGP entry (SBX-03 / §2.8) */}
      {tab === "odds" && (
        <div className="mt-4 space-y-5">
          <div>
            <h2 className="font-display text-2xl font-black tracking-[-.02em] sm:text-[28px]">Featured markets</h2>
            <p className="mt-0.5 text-sm text-[#71717a]">Game lines, then this game&rsquo;s player props</p>
          </div>

          {hasLines ? (
            <div ref={(el) => (sectionRefs.current.lines = el)} className="scroll-mt-28 overflow-hidden rounded-[18px]" style={{ background: "linear-gradient(145deg, hsl(var(--ink-700)) 0%, hsl(var(--surface-3)) 45%, hsl(var(--ink-800)) 100%)", boxShadow: "inset 0 1px 0 hsl(var(--edge-light)), inset 0 -1px 0 hsl(var(--edge-dark)), var(--elev-2)", border: "1px solid hsl(var(--surface-border))" }} data-testid="section-lines">
              <GameLinesHeader sport={ev.sport} />
              <div className="hidden lg:block"><GameMarketRow event={lineEvent} onPick={(e, o) => choose(o)} isPicked={(eid, oid) => picked(oid)} showMeta={false} mobile={false} /></div>
              <div className="lg:hidden"><GameMarketRow event={lineEvent} onPick={(e, o) => choose(o)} isPicked={(eid, oid) => picked(oid)} showMeta={false} mobile /></div>
            </div>
          ) : (
            <div className="rounded-2xl border border-[#1F1F23] bg-[#101013] px-4 py-12 text-center text-sm text-[#71717a]" data-testid="event-odds-empty">No game lines posted for this event right now.</div>
          )}

          {popularPreview.length > 0 && (
            <div className="overflow-hidden rounded-[18px]" style={{ background: "linear-gradient(145deg, hsl(var(--ink-700)) 0%, hsl(var(--surface-3)) 45%, hsl(var(--ink-800)) 100%)", boxShadow: "inset 0 1px 0 hsl(var(--edge-light)), inset 0 -1px 0 hsl(var(--edge-dark)), var(--elev-2)", border: "1px solid hsl(var(--surface-border))" }} data-testid="section-props-preview">
              <div className="flex items-center justify-between px-4 pb-2 pt-3.5 sm:px-5">
                <div className="flex items-baseline gap-2">
                  <h3 className="font-display text-lg font-extrabold tracking-[-.02em]">Player props</h3>
                  <span className="text-xs text-[#71717a]">{popularPreview.length} of {totalPropLines} lines</span>
                </div>
                <button onClick={() => setTab("props")} className="flex items-center gap-1 rounded-md text-sm font-bold text-pnr-green focus-ring" data-testid="event-all-props">All player props <Icon name="chevron-right" size={14} /></button>
              </div>
              {popularPreview.map((pp, i) => {
                const s = pp.row.standard;
                const a0 = (pp.row.alts || [])[0];
                const over = s ? (s.yesno ? s.yes : s.over) : (a0 ? a0.outcome : null);
                const under = s ? (s.yesno ? s.no : s.under) : null;
                const line = s ? (s.yesno ? undefined : s.line) : (a0 ? a0.outcome.line : undefined);
                const yesno = !!(s && s.yesno);
                if (!over && !under) return null;
                const props = { player: pp.row.player, propLabel: pp.label, line, over, under, yesno, onPick: choose, picked: (o) => pickedO(o), last: i === popularPreview.length - 1 };
                return (
                  <div key={i}>
                    <div className="hidden lg:block"><PlayerPropRow {...props} /></div>
                    <div className="lg:hidden"><PlayerPropRow mobile {...props} /></div>
                  </div>
                );
              })}
            </div>
          )}

          {!locked && (
            <div className="surface-premium flex items-center justify-between gap-3 rounded-[18px] p-4 sm:px-5" data-testid="event-sgp-entry">
              <div className="flex min-w-0 items-center gap-3">
                <div className="min-w-0">
                  <p className="font-display text-[15px] font-extrabold tracking-tight">Same Game Parlay</p>
                  <p className="truncate text-xs text-[#71717a]">Combine one game line with player props from this game.</p>
                </div>
              </div>
              <button onClick={() => setTab("sgp")} className="flex h-11 shrink-0 items-center rounded-xl px-4 font-display text-sm font-extrabold text-[#0A0A0A] transition-colors hover:bg-white focus-ring" style={{ background: "#FAFAFA" }} data-testid="event-build-sgp">Build Parlay</button>
            </div>
          )}
        </div>
      )}

      {/* PLAYER PROPS — honest "None available" empty state when there are none */}
      {tab === "props" && (
        <div ref={(el) => (sectionRefs.current.props = el)} className="mt-4 scroll-mt-28 space-y-3" data-testid="section-props">
          {players.length === 0 ? (
            <div className="rounded-[18px] border border-[#1F1F23] bg-[#101013] px-4 py-12 text-center text-sm text-[#71717a]" data-testid="event-props-empty">No player props posted for this event right now.</div>
          ) : (
            <>
              {/* search (convenience, not the primary way to find a market) */}
              <div className="relative">
                <input type="text" value={propSearch} onChange={(e) => setPropSearch(e.target.value)}
                  placeholder="Search player or market" data-testid="prop-search" aria-label="Search player or market"
                  className="h-11 w-full rounded-xl bg-[#0E0E11] pl-10 pr-9 text-[15px] text-white placeholder-[#52525b] outline-none focus-ring"
                  style={{ boxShadow: "inset 0 0 0 1px #26262B" }} />
                <Icon name="search" size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-pnr-green" />
                {propSearch && (
                  <button onClick={() => setPropSearch("")} data-testid="prop-search-clear" aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md text-[#71717a] hover:text-white focus-ring">
                    <Icon name="x" size={15} />
                  </button>
                )}
              </div>
              {/* CATEGORY chips (market-first) — the primary way to reach a market */}
              {!q && catalog.categories.length > 0 && (
                <div className="no-scrollbar -mr-4 flex gap-2 overflow-x-auto pb-1 sm:mr-0" data-testid="prop-categories">
                  {catalog.categories.map((c) => (
                    <PropChip key={c.key} label={c.label} active={activeCat?.key === c.key} onClick={() => setPropCat(c.key)} />
                  ))}
                </div>
              )}
              {/* markets for the active category (Market → Players → lines/thresholds), or search results.
                  MarketProps stays MOUNTED even when the set is momentarily empty (it renders null) so a
                  live refresh that briefly empties the category can't unmount it and wipe the user's open
                  market / Main-Alt mode / selected threshold. */}
              {shownMarkets.length === 0 && (
                <div className="rounded-[18px] border border-[#1F1F23] bg-[#101013] px-4 py-10 text-center text-sm text-[#71717a]" data-testid="prop-no-results">
                  {q ? <>No players or markets match &ldquo;{propSearch}&rdquo;.</> : "No markets in this category right now."}
                </div>
              )}
              <MarketProps markets={shownMarkets} onPick={choose} picked={(o) => pickedO(o)} />
            </>
          )}
        </div>
      )}

      {/* SAME GAME PARLAY — real builder: one game line + props, one side each (§4, SBX-05) */}
      {tab === "sgp" && (
        <div className="mt-4" data-testid="event-sgp">
          {hasLines || players.length ? (
            <SGPBuilder event={{ ...ev, id }} groups={groups} players={players} choose={choose} has={(oid) => picked(oid)} eventLabel={eventLabel} />
          ) : (
            <div className="rounded-[18px] border border-[#1F1F23] bg-[#101013] px-4 py-12 text-center text-sm text-[#71717a]" data-testid="event-sgp-empty">No markets available to build a same-game parlay for this event right now.</div>
          )}
        </div>
      )}
    </div>
  );
}
